import React from 'react'
import PropTypes from 'prop-types'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import IconButton from '@material-ui/core/IconButton'
import Dialog from '@material-ui/core/Dialog'
import DialogContent from '@material-ui/core/DialogContent'
import { makeStyles } from '@material-ui/core/styles'
import clsx from 'clsx'
import CloseIcon from 'components/material-symbol/icons/Close'
import CircularHighlightedIcon from 'components/circular-highlighted-icon'
import {
  PROMO_CALLOUT_BORDER_RADIUS,
  CALLOUT_BACKGROUND,
  CALLOUT_FOREGROUND,
} from 'lib/form-field-styles'

const useStyles = makeStyles(theme => ({
  paper: {
    width: '100%',
    maxWidth: 1040,
    borderRadius: PROMO_CALLOUT_BORDER_RADIUS,
  },
  closeButton: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    zIndex: 1,
    color: theme.palette.text.primary,
  },
  content: {
    position: 'relative',
    padding: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(3),
    },
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 600,
    lineHeight: 1.3,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
    paddingRight: theme.spacing(7),
  },
  subtitle: {
    fontSize: '1rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
    lineHeight: 1.5,
  },
  cardsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '1fr',
    },
  },
  cardsRowTwoUp: {
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '1fr',
    },
  },
  cardColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minWidth: 0,
  },
  card: {
    width: '100%',
    minHeight: 220,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(3, 2.5, 2.5),
    borderRadius: PROMO_CALLOUT_BORDER_RADIUS,
    border: '1px solid #D5DDE3',
    backgroundColor: theme.palette.common.white,
    cursor: 'pointer',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
      borderColor: '#9AA8B3',
    },
  },
  cardTitle: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    fontSize: '1.125rem',
    fontWeight: 600,
    lineHeight: 1.3,
    color: '#00234B',
    textAlign: 'center',
    textTransform: 'none',
  },
  cardDescription: {
    fontSize: '0.875rem',
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
    textAlign: 'center',
  },
}))

const ChoiceStartCard = ({
  icon: Icon,
  title,
  description,
  onClick,
  classes,
}) => (
  <Box className={classes.cardColumn}>
    <Box
      className={classes.card}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onClick()
        }
      }}
    >
      <CircularHighlightedIcon
        icon={Icon}
        size={56}
        iconSize={28}
        highlightColor={CALLOUT_BACKGROUND}
        iconColor={CALLOUT_FOREGROUND}
      />
      <Typography className={classes.cardTitle}>{title}</Typography>
      <Typography component="div" className={classes.cardDescription}>
        {description}
      </Typography>
    </Box>
  </Box>
)

ChoiceStartCard.propTypes = {
  icon: PropTypes.elementType.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.node.isRequired,
  onClick: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
}

const ChoiceStartModal = ({
  open,
  onClose,
  title,
  subtitle,
  options,
  columns = 3,
}) => {
  const classes = useStyles()

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      classes={{ paper: classes.paper }}
      aria-labelledby="choice-start-modal-title"
    >
      <DialogContent className={classes.content}>
        <IconButton
          className={classes.closeButton}
          aria-label="Close"
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>

        <Typography
          id="choice-start-modal-title"
          className={classes.title}
          component="h1"
        >
          {title}
        </Typography>
        {subtitle ? (
          <Typography className={classes.subtitle}>{subtitle}</Typography>
        ) : null}

        <Box
          className={clsx(
            classes.cardsRow,
            columns === 2 && classes.cardsRowTwoUp,
          )}
        >
          {options.map(option => (
            <ChoiceStartCard key={option.id} {...option} classes={classes} />
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  )
}

ChoiceStartModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      icon: PropTypes.elementType.isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.node.isRequired,
      onClick: PropTypes.func.isRequired,
    }),
  ).isRequired,
  columns: PropTypes.oneOf([2, 3]),
}

export default ChoiceStartModal
